import { useEffect } from 'react';
import { graphql, readInlineData } from 'react-relay';
import { updateRecentSearchResultEntry } from 'dibs-buyer-layout/exports/sharedSearchBarHelpers.ts';
import { getFilterValue } from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers.ts';
import { FILTER_SEARCH } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants.ts';

import { type useUpdateRecentSearchResultEntryFromItemSearch_itemSearch$key } from './__generated__/useUpdateRecentSearchResultEntryFromItemSearch_itemSearch.graphql.ts';

/* eslint-disable relay/unused-fields -- appliedFilters read via getFilterValue */
const useUpdateRecentSearchResultEntryFromItemSearchFragment = graphql`
    fragment useUpdateRecentSearchResultEntryFromItemSearch_itemSearch on ItemSearchQueryConnection
    @inline {
        totalResults
        searchTerm
        searchCorrections {
            originalSearchTerm
            semanticSearchTerm
            classificationSearchTerm
        }
        appliedFilters {
            name
            values {
                urlLabel
            }
        }
    }
`;
/* eslint-enable relay/unused-fields */

type Params = {
    isClient: boolean;
    itemSearch: useUpdateRecentSearchResultEntryFromItemSearch_itemSearch$key | null | undefined;
};

export function useUpdateRecentSearchResultEntryFromItemSearch({
    isClient,
    itemSearch,
}: Params): void {
    useEffect(() => {
        if (!isClient) {
            return;
        }

        const fragmentData = readInlineData(
            useUpdateRecentSearchResultEntryFromItemSearchFragment,
            itemSearch
        );
        const { appliedFilters, searchCorrections, searchTerm, totalResults } = fragmentData || {};

        if (!totalResults) {
            return;
        }

        // search from url determines that we came to the page via search bar, so we can use it to update the recent search results
        const searchFromUrl =
            new URLSearchParams(window.location.search).get(FILTER_SEARCH)?.trim() || null;

        const { originalSearchTerm, semanticSearchTerm, classificationSearchTerm } =
            searchCorrections || {};
        const oqUrlLabel = getFilterValue(appliedFilters ?? [], 'originalSearch')?.urlLabel ?? null;

        const hasSearchTerm = !!(
            searchFromUrl ||
            searchTerm ||
            oqUrlLabel ||
            originalSearchTerm ||
            semanticSearchTerm ||
            classificationSearchTerm
        );

        if (!hasSearchTerm) {
            return;
        }

        const hasSpellCorrection = !!originalSearchTerm;
        updateRecentSearchResultEntry({
            resultCount: totalResults,
            originalSearchTerm:
                oqUrlLabel ||
                originalSearchTerm ||
                searchTerm ||
                searchFromUrl ||
                classificationSearchTerm,
            correctedTerm: hasSpellCorrection ? searchTerm : undefined,
        });
    }, [isClient, itemSearch]);
}
